import { Link, Navigate, useParams } from 'react-router-dom';
import SEO from '@/components/SEO';
import { locationBySlug, locations } from '@/data/locations';
import { ArrowRight, CheckCircle, Clock, MapPin, Phone, Shield, Truck, Wrench } from 'lucide-react';

const repairs = [
  'DLP / DMD chip replacement for white dots',
  'Power supply, ballast and no-start faults',
  'Overheating, fan and thermal shutdown repair',
  'LCD polariser, prism and colour discolouration',
  'Laser light-source and phosphor-wheel faults',
  'HDMI, control-board and network AV faults',
];

export default function LocationPage() {
  const { slug = '' } = useParams();
  const location = locationBySlug(slug);

  if (!location) return <Navigate to="/locations" replace />;

  const canonical = `https://projectorrepairlondon.co.uk/locations/${location.slug}`;
  const related = locations.filter((l) => l.slug !== location.slug && (l.region === location.region || l.county === location.county)).slice(0, 6);

  return (
    <>
      <SEO
        title={`Projector Repair ${location.name}`}
        description={`Projector repair in ${location.name}, ${location.county}. Free insured collection, component-level diagnostics, 90-day warranty and 4-7 day turnaround across ${location.postcodes}.`}
        canonical={canonical}
        extraSchemas={[
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: `Projector Repair in ${location.name}`,
            provider: { '@id': 'https://projectorrepairlondon.co.uk/#localbusiness' },
            areaServed: [
              { '@type': 'City', name: location.name },
              { '@type': 'Place', name: location.county },
              { '@type': 'Place', name: location.region },
            ],
            offers: {
              '@type': 'Offer',
              priceCurrency: 'GBP',
              availability: 'https://schema.org/InStock',
              itemOffered: { '@type': 'Service', name: 'Projector diagnostics and repair' },
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: `Do you collect projectors in ${location.name}?`,
                acceptedAnswer: { '@type': 'Answer', text: `Yes. We arrange insured courier collection across ${location.name} and nearby areas including ${location.nearby.slice(0, 3).join(', ')}.` },
              },
              {
                '@type': 'Question',
                name: `How long does projector repair take in ${location.name}?`,
                acceptedAnswer: { '@type': 'Answer', text: 'Most repairs are completed within 4-7 working days after quote approval, depending on parts availability and fault complexity.' },
              },
            ],
          },
        ]}
      />

      <section className="bg-[#0057B7] pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container-custom">
          <nav className="mb-6"><ol className="flex flex-wrap items-center gap-2 text-sm">
            <li><Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link></li>
            <li className="text-white/40">/</li>
            <li><Link to="/locations" className="text-white/70 hover:text-white transition-colors">Locations</Link></li>
            <li className="text-white/40">/</li>
            <li className="text-white font-medium">{location.name}</li>
          </ol></nav>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full text-xs font-label text-[#FFDD00] mb-4"><MapPin className="w-3.5 h-3.5" />{location.county} • {location.postcodes}</span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white max-w-3xl leading-tight">Projector Repair {location.name}</h1>
          <p className="mt-4 text-white/80 max-w-3xl leading-relaxed">{location.intro}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/repair-enquiry" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#FFDD00] text-[#0057B7] rounded-lg font-bold hover:bg-[#C9A800] transition-all">Get Free Collection <ArrowRight className="w-5 h-5" /></Link>
            <a href="tel:+443330064326" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-all border border-white/20"><Phone className="w-5 h-5" />0333 006 4326</a>
          </div>
        </div>
      </section>

      <section className="bg-white py-14 lg:py-18">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10">
          <div>
            <span className="font-label text-[#0057B7]">Local Service</span>
            <h2 className="font-heading text-3xl font-bold text-[#0057B7] mt-3">Collection & Repair Coverage in {location.name}</h2>
            <p className="mt-4 text-[#0057B7]/80 leading-relaxed">We collect from homes, schools, offices, venues, cinemas and AV integrators across {location.name} and {location.county}. Your projector is logged, insured and transported to our ISO-certified Doncaster laboratory for diagnosis. You receive a fixed-price quote before any paid repair work begins.</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[{ icon: Truck, label: 'Free insured collection' }, { icon: Shield, label: '90-day warranty' }, { icon: Clock, label: '4-7 day turnaround' }].map((f) => (
                <div key={f.label} className="bg-[#E8F0FE] rounded-xl p-4 border border-[#0057B7]/10 text-[#0057B7] text-sm font-medium flex items-center gap-2"><f.icon className="w-4 h-4" />{f.label}</div>
              ))}
            </div>
            <h3 className="font-heading text-xl font-semibold text-[#0057B7] mt-10 mb-4">Nearby Areas Covered</h3>
            <div className="flex flex-wrap gap-3">
              {location.nearby.map((n) => <span key={n} className="px-4 py-2 bg-white border border-[#0057B7]/15 rounded-full text-sm text-[#0057B7]/80">{n}</span>)}
            </div>
          </div>
          <div className="bg-[#E8F0FE] rounded-2xl p-7 border border-[#0057B7]/10 h-fit">
            <span className="font-label text-[#0057B7]">Common Repairs</span>
            <h2 className="font-heading text-2xl font-bold text-[#0057B7] mt-3">Fixed in Our Laboratory</h2>
            <ul className="mt-6 space-y-3">
              {repairs.map((r) => <li key={r} className="flex items-start gap-3 text-sm text-[#0057B7]/80"><CheckCircle className="w-4 h-4 mt-0.5 text-[#0057B7]" />{r}</li>)}
            </ul>
            <Link to="/repair-enquiry" className="mt-7 inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 bg-[#0057B7] text-[#FFDD00] rounded-lg font-bold hover:bg-[#003D80] transition-colors"><Wrench className="w-4 h-4" />Start Repair Enquiry</Link>
          </div>
        </div>
      </section>

      <section className="bg-[#E8F0FE] py-14">
        <div className="container-custom">
          <div className="flex items-end justify-between gap-6 mb-7">
            <div>
              <span className="font-label text-[#0057B7]">Nearby Service Areas</span>
              <h2 className="font-heading text-2xl font-bold text-[#0057B7] mt-2">Also Serving</h2>
            </div>
            <Link to="/locations" className="text-[#0057B7] font-semibold inline-flex items-center gap-2">All locations <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {related.map((l) => (
              <Link key={l.slug} to={`/locations/${l.slug}`} className="bg-white rounded-xl p-5 border border-[#0057B7]/10 hover:border-[#0057B7]/30 transition-colors">
                <h3 className="font-heading font-semibold text-[#0057B7]">Projector Repair {l.name}</h3>
                <p className="mt-1 text-sm text-[#0057B7]/70">{l.county} • {l.postcodes}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FFDD00] py-14">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl font-bold text-[#0057B7]">Book Projector Repair in {location.name}</h2>
          <p className="mt-3 text-[#0057B7]/80 max-w-2xl mx-auto">Tell us the brand, model, fault and postcode. We will confirm collection and send your repair reference.</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
            <Link to="/repair-enquiry" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0057B7] text-[#FFDD00] rounded-lg font-bold hover:bg-[#003D80] transition-all text-lg">Repair Enquiry <ArrowRight className="w-5 h-5" /></Link>
            <a href="mailto:wellselectronicsltd@gmail.com" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0057B7] rounded-lg font-semibold hover:bg-[#E8F0FE] transition-all text-lg border border-[#0057B7]/20">wellselectronicsltd@gmail.com</a>
          </div>
        </div>
      </section>
    </>
  );
}
